import { useState } from "react";
import "./Contact.css";
import PageHero from "../../components/PageHero/PageHero";

const SUBJECTS = ['Project', 'Career', 'General'];

function Contact({ content, loading }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Project',
    message: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  if (loading || !content) {
    return <div className="contact-page">Loading...</div>;
  }

  const contactInfo = content.footer?.contactInfo || {};
  const contactPerson = content.footer?.contactPerson || {};

  // Validate a single field — returns error string or ''
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length > 100) return 'Name must be under 100 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (value && value.length > 30) return 'Phone number is too long';
        return '';
      case 'subject':
        if (!SUBJECTS.includes(value)) return 'Please select a subject';
        return '';
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        if (value.trim().length > 2000) return 'Message must be under 2000 characters';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value);
    if (err) setFieldErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Full validation on submit
    const errors = {};
    Object.keys(form).forEach(key => {
      const err = validateField(key, form[key]);
      if (err) errors[key] = err;
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
      } else if (res.status === 429) {
        setStatus('error');
        setErrorMsg("You've sent too many messages recently. Please wait an hour before trying again.");
      } else if (res.status === 400 && data.fields) {
        setStatus('idle');
        setFieldErrors(data.fields);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again, or email us directly.');
    }
  };

  const handleSendAnother = () => {
    setStatus('idle');
    setForm({ name: '', email: '', phone: '', subject: 'Project', message: '' });
    setFieldErrors({});
    setErrorMsg('');
  };

  return (
    <div className="contact-page">

      {/* HERO */}
      <PageHero
        title={content.pageHero?.contact?.title}
        media={content.pageHero?.contact?.media}
      />

      {/* CONTACT INFO BLOCK */}
      <section className="block contact-info-block">
        <div className="contact-info-content">

          {/* LEFT - COMPANY */}
          <div className="contact-column">
            <h3>Contact Information</h3>

            {contactInfo.address && <p>📍 {contactInfo.address}</p>}

            {(contactInfo.emails || []).map((email, index) => (
              <span key={index}>
                <a href={`mailto:${email}`}>📧 {email}</a>
                <br />
              </span>
            ))}

            {contactInfo.phone && (
              <>
                <a href={`tel:${contactInfo.phone}`}>📞 {contactInfo.phone}</a>
                <br />
              </>
            )}

            {contactInfo.website && (
              <a href={`https://${contactInfo.website}`} target="_blank" rel="noreferrer">
                🌐 {contactInfo.website}
              </a>
            )}
          </div>

          {/* RIGHT - PERSON */}
          <div className="contact-column contact-person">
            <h3>Direct Contact</h3>

            {contactPerson.name && <p className="person-name">{contactPerson.name}</p>}
            {contactPerson.title && <p>{contactPerson.title}</p>}

            {contactPerson.phone && (
              <>
                <a href={`tel:${contactPerson.phone}`}>📞 {contactPerson.phone}</a>
                <br />
              </>
            )}

            {contactPerson.email && (
              <a href={`mailto:${contactPerson.email}`}>📧 {contactPerson.email}</a>
            )}
          </div>

        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="block contact-form-block">
        <h2>SEND US A MESSAGE</h2>

        {/* SUCCESS STATE */}
        {status === 'success' ? (
          <div className="contact-success">
            <div className="contact-success-icon">✅</div>
            <h3>Message sent!</h3>
            <p>
              Thank you, <strong>{form.name.split(' ')[0]}</strong>. We received your message
              and will get back to you within 24–48 hours.
            </p>
            <button className="contact-send-another" onClick={handleSendAnother}>
              Send another message →
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit} noValidate>

            {/* Global error banner */}
            {status === 'error' && (
              <div className="contact-error-banner" role="alert">
                {errorMsg}{' '}
                {contactInfo.emails?.[0] && (
                  <a href={`mailto:${contactInfo.emails[0]}`}>
                    Email us directly
                  </a>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-field">
                <input
                  type="text"
                  name="name"
                  placeholder="Name *"
                  value={form.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.name ? 'field-error' : ''}
                  disabled={status === 'submitting'}
                  aria-required="true"
                  aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                />
                {fieldErrors.name && (
                  <span className="field-error-msg" id="name-error" role="alert">
                    {fieldErrors.name}
                  </span>
                )}
              </div>

              <div className="form-field">
                <input
                  type="email"
                  name="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.email ? 'field-error' : ''}
                  disabled={status === 'submitting'}
                  aria-required="true"
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
                {fieldErrors.email && (
                  <span className="field-error-msg" id="email-error" role="alert">
                    {fieldErrors.email}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.phone ? 'field-error' : ''}
                  disabled={status === 'submitting'}
                />
                {fieldErrors.phone && (
                  <span className="field-error-msg" role="alert">{fieldErrors.phone}</span>
                )}
              </div>

              <div className="form-field">
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  disabled={status === 'submitting'}
                  aria-required="true"
                >
                  {SUBJECTS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-field">
              <textarea
                name="message"
                placeholder="Message *"
                value={form.message}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldErrors.message ? 'field-error' : ''}
                disabled={status === 'submitting'}
                rows={5}
                aria-required="true"
                aria-describedby={fieldErrors.message ? 'message-error' : undefined}
              />
              <div className="message-footer">
                {fieldErrors.message && (
                  <span className="field-error-msg" id="message-error" role="alert">
                    {fieldErrors.message}
                  </span>
                )}
                <span className="char-count">{form.message.length}/2000</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="contact-submit-btn"
            >
              {status === 'submitting' ? (
                <><span className="btn-spinner" aria-hidden="true"></span> Sending…</>
              ) : 'SUBMIT'}
            </button>

            <p className="form-note">
              Our team will respond within 24–48 hours.
            </p>

          </form>
        )}
      </section>

      {/* MAP */}
      <section className="block map-block">
        <iframe
          title="Company Location"
          src="https://www.google.com/maps?q=Tunis,Tunisia&output=embed"
          loading="lazy"
        ></iframe>
      </section>

    </div>
  );
}

export default Contact;
